/**
 * Pure live-motion logic for System Status (Flow L §8/§9/§11; ADR-035 / S6.6).
 *
 * No React — the state-transition diff, the connection-churn burst detector, and
 * the sustained-failover tracker, plus the motion timing constants. Kept pure so
 * they are unit-testable without the DOM and reusable by :func:`useStatusMotion`.
 *
 * Cross-references:
 *   - Flow L §8 (transition treatment for in-flight failover) / §9 (interaction)
 *     / §11 (connection churn, sustained failover).
 *   - REQ-STATUS-016 (never a false green — the tracker escalates non-healthy).
 */
import type { RollupState } from "@/system-status/status-model";

/** How long a stoplight-transition pulse lingers before it clears (ms). */
export const TRANSITION_FLASH_MS = 1200;
/** How long a connection-burst highlight lingers (ms) — longer, so it's noticed. */
export const BURST_FLASH_MS = 4000;
/** New ingress connections within one update that count as a "burst" (§11). */
export const BURST_THRESHOLD = 3;
/** How long a component must stay non-healthy before it escalates (ms; §11). */
export const SUSTAINED_THRESHOLD_MS = 30_000;
/** Re-check cadence for sustained failover (state may not change while stuck). */
export const SUSTAINED_RECHECK_MS = 3_000;

/**
 * Node ids whose stoplight state changed between two snapshots — the animation
 * fires only for a node present in **both** maps with a different state (so the
 * initial paint, where ``prev`` is empty, produces none — no flashing storm on
 * open, per §9).
 */
export function changedNodeIds(
  prev: ReadonlyMap<string, RollupState>,
  curr: ReadonlyMap<string, RollupState>,
): string[] {
  const ids: string[] = [];
  for (const [id, state] of curr) {
    const before = prev.get(id);
    if (before !== undefined && before !== state) ids.push(id);
  }
  return ids;
}

/**
 * Whether a connections node saw a burst — its total jumped by at least
 * ``threshold`` since the last update (§11; ties to ``producer.first_sighting``).
 * Null totals ("not collected") never burst.
 */
export function isConnectionBurst(
  prevTotal: number | null | undefined,
  currTotal: number | null | undefined,
  threshold: number = BURST_THRESHOLD,
): boolean {
  if (prevTotal === null || prevTotal === undefined) return false;
  if (currTotal === null || currTotal === undefined) return false;
  return currTotal - prevTotal >= threshold;
}

/** Connections-node ids whose total burst since the last update. */
export function burstNodeIds(
  prev: ReadonlyMap<string, number | null>,
  curr: ReadonlyMap<string, number | null>,
  threshold: number = BURST_THRESHOLD,
): string[] {
  const ids: string[] = [];
  for (const [id, total] of curr) {
    if (isConnectionBurst(prev.get(id), total, threshold)) ids.push(id);
  }
  return ids;
}

/** A node's current state + when it entered it (for the sustained-failover clock). */
export interface StateSince {
  state: RollupState;
  since: number;
}

/**
 * Advance the state-since tracker: a node keeps its ``since`` while its state is
 * unchanged, resets ``since`` to ``now`` when it changes / first appears, and is
 * dropped when it leaves the current set. Pure — the hook holds the map in a ref.
 */
export function advanceStateTracker(
  prev: ReadonlyMap<string, StateSince>,
  currStates: ReadonlyMap<string, RollupState>,
  now: number,
): Map<string, StateSince> {
  const next = new Map<string, StateSince>();
  for (const [id, state] of currStates) {
    const before = prev.get(id);
    next.set(id, before !== undefined && before.state === state ? before : { state, since: now });
  }
  return next;
}

const NON_HEALTHY: ReadonlySet<RollupState> = new Set(["degraded", "unhealthy"]);

/**
 * Node ids that have been in a **non-healthy** state (degraded / unhealthy)
 * continuously for at least ``threshold`` — a sustained failover, not a
 * momentary blip (§11). These escalate the transition treatment amber→red +
 * surface a banner.
 */
/**
 * How long each sustained node has been non-healthy, in milliseconds.
 *
 * The duration is the substance of a sustained escalation — "degraded for four
 * minutes" is a materially different operator situation from "degraded for four
 * seconds", and the instantaneous rollup cannot express it. It is surfaced on the
 * node's marker tooltip and accessible name rather than in a banner
 * (architect, 2026-08-01).
 */
export function sustainedDurations(
  tracker: ReadonlyMap<string, StateSince>,
  now: number,
  threshold: number = SUSTAINED_THRESHOLD_MS,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const [id, { state, since }] of tracker) {
    const elapsed = now - since;
    if (NON_HEALTHY.has(state) && elapsed >= threshold) out.set(id, elapsed);
  }
  return out;
}

export function sustainedNodeIds(
  tracker: ReadonlyMap<string, StateSince>,
  now: number,
  threshold: number = SUSTAINED_THRESHOLD_MS,
): string[] {
  const ids: string[] = [];
  for (const [id, { state, since }] of tracker) {
    if (NON_HEALTHY.has(state) && now - since >= threshold) ids.push(id);
  }
  return ids;
}
